import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import {
  Input,
  Textarea,
  Label,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PersonAvatar } from "@/components/ui/person-avatar";
import { AdminPurchasedPackageCard } from "@/components/packages/AdminPurchasedPackageCard";
import PackageSelectionDialog from "@/pages/Admin/Students/Edit/PackageSelectionDialog";
import { useAppToast } from "@/hooks/useAppToast";
import {
  getStudentById,
  updateStudent,
  addPackagesToStudent,
  removePackageFromStudent,
  resetStudentPassword
} from "@/services/admin/student.service";
import { resendConfirmationEmail } from "@/services/shared/auth/auth.service";
import { Student, StudentPackage } from "@/services/admin/common.interfaces";
import { Package } from "@/services/admin/packages.interfaces";
import { useAutoSave } from "@/hooks/useAutoSave";
import { AutoSaveForm } from "@/components/forms/AutoSaveForm";
import { SaveStatus } from "@/types/common";
import { SaveIndicator } from "@/components/forms/SaveIndicator";
import { Link, useNavigate } from "react-router-dom";

const EditStudent: React.FC = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const toast = useAppToast();
  const [isLoading, setIsLoading] = useState(true);
  const [studentData, setStudentData] = useState<Student | null>(null);
  const [isPackageDialogOpen, setIsPackageDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<StudentPackage | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statusValue, setStatusValue] = useState<"Active" | "Inactive">("Active");
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [packageSearchTerm, setPackageSearchTerm] = useState(""); // State for package search term
  const [blockAccessDialogOpen, setBlockAccessDialogOpen] = useState(false); // State for block access dialog
  const [studentSaveStatus, setStudentSaveStatus] = useState<SaveStatus>({ status: 'idle' });
  const [resendEmailLoading, setResendEmailLoading] = useState(false);


  const handleProgrammaticResetPassword = async () => {
    if (studentId) {
      try {
        await resetStudentPassword(studentId);
        const updatedStudent = await getStudentById(studentId);
        setStudentData(updatedStudent);
        toast.success("Password reset programmatically triggered");
      } catch (error) {
        console.error("Error resetting password programmatically:", error);
        toast.error("Failed to reset password programmatically");
      }
    }
  };

  useEffect(() => {
    const handleCustomEvent = () => {
      handleProgrammaticResetPassword();
    };

    window.addEventListener('resetPasswordEvent', handleCustomEvent);

    return () => {
      window.removeEventListener('resetPasswordEvent', handleCustomEvent);
    };
  }, [studentId]);

  useEffect(() => {
    const loadStudent = async () => {
      if (!studentId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await getStudentById(studentId);
        setStudentData(data);
        setStatusValue(data?.status as "Active" | "Inactive" || "Active");
        setNotes(data?.notes || "");
        setName(data?.name || "");
        setEmail(data?.email || "");
      } catch (error) {
        console.error("Error loading student:", error);
        toast.error("Failed to load student data");
      } finally {
        setIsLoading(false);
      }
    };

    loadStudent();
  }, [studentId]);

  useEffect(() => {
    setStatusValue(studentData?.status as "Active" | "Inactive" || "Active");
  }, [studentData]);

  useEffect(() => {
    setNotes(studentData?.notes || "");
  }, [studentData?.notes]);

  useEffect(() => {
    setName(studentData?.name || "");
  }, [studentData?.name]);

  useEffect(() => {
    setEmail(studentData?.email || "");
  }, [studentData?.email]);


  const saveNotes = useCallback(async (currentNotes: string) => {
    if (!studentId) return;
    try {
      setIsSaving(true);
      // Call API to save notes here, using studentId and currentNotes
      console.log("Saving notes:", currentNotes); // Placeholder for API call
      // await updateStudentNotes(studentId, currentNotes); // Hypothetical API call
      toast.success("Notes saved automatically");
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Failed to save notes automatically");
    } finally {
      setIsSaving(false);
    }
  }, [studentId, toast]);

  const autoSaveNotes = useAutoSave({ // Corrected useAutoSave call with options object
    onSave: saveNotes,
    delay: 1000,
  });

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
    autoSaveNotes(e.target.value);
  };

  const handleBlockAccess = async () => {
    setBlockAccessDialogOpen(false); // Close dialog after action
    // Implement block access logic here
    toast.success("Access blocked successfully"); // Placeholder success message
  };


  const handleRemovePackage = async (packageId: string) => {
    if (!studentId) return;

    try {
      await removePackageFromStudent(studentId, packageId);
      const updatedStudent = await getStudentById(studentId);
      setStudentData(updatedStudent);
      toast.success("Package removed successfully");
    } catch (error) {
      console.error("Error removing package:", error);
      toast.error("Failed to remove package");
    }
  };

  const saveStudentDetails = useCallback(async (data: { name: string, email: string, status: "Active" | "Inactive" }) => {
    if (!studentId) return;
    try {
      setStudentSaveStatus({ status: 'saving' });
      await updateStudent(studentId, data);
      const updatedStudent = await getStudentById(studentId);
      setStudentData(updatedStudent);
      setStudentSaveStatus({ status: 'saved', lastSaved: new Date() });
      toast.success("Student details saved automatically");
    } catch (error) {
      console.error("Error saving student details:", error);
      setStudentSaveStatus({ status: 'error', error: error instanceof Error ? error.message : 'Save failed' });
      toast.error("Failed to save student details automatically");
    }
  }, [studentId, toast]);

  const autoSaveStudentDetails = useAutoSave({
    onSave: saveStudentDetails,
    delay: 1000,
    onStatusChange: setStudentSaveStatus,
    onError: (error) => {
      console.error('Auto-save error:', error);
    }
  });


  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleStatusChange = (value: string) => {
    setStatusValue(value as "Active" | "Inactive");
  };


  const handleSaveChanges = async () => {
    if (!studentId || !studentData) return;

    try {
      setIsSaving(true);
      await updateStudent(studentId, {
        name,
        email,
        status: statusValue
      });
      const updatedStudent = await getStudentById(studentId);
      setStudentData(updatedStudent);
      toast.success("Changes saved successfully");
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || !studentData) {
    return (
      <AdminPageLayout title="Loading...">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout title={`Edit ${studentData.name}`}>
      <main id="main-content" className="mx-auto py-8">
        <div className="mb-6 flex items-center space-x-4">
          <button
            onClick={() => navigate("/admin/access/students")}
            className="flex items-center space-x-2 text-neutral-600 hover:text-neutral-900"
          >
            <i className="fa-solid fa-arrow-left"></i>
            <span>Back to Students</span>
          </button>
        </div>
        <div id="student-header" className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <PersonAvatar
              size="xl"
              userId={studentId}
              userName={studentData.name}
              avatarUrl={studentData.avatar?.url}
            />
            <h1 className="text-2xl font-semibold">Edit Student Profile</h1>
          </div>
          <div className="flex items-center space-x-3">
            <Link to={`/admin/access/students/${studentId}/level-test`}>
              <Button variant="outline" className="space-x-2">
                <i className="fa-solid fa-clipboard-question"></i>
                <span>Level Test</span>
              </Button>
            </Link>
            <Button variant="outline" className="space-x-2">
              <i className="fa-solid fa-message"></i>
              <span>Send Message</span>
            </Button>
          </div>
        </div>
        <AutoSaveForm
          onSave={async (data: { name: string, email: string, status: "Active" | "Inactive" }) => {
            autoSaveStudentDetails({ name: data.name, email: data.email, status: data.status });
          }}
        >
        <div id="student-form" className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
          <div className="mb-4 flex justify-end">
              <SaveIndicator status={studentSaveStatus} />
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="full-name">Full Name</Label>
              <Input
                id="full-name"
                type="text"
                name="name"
                value={name}
                onChange={handleNameChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                name="email"
                value={email}
                onChange={handleEmailChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start space-x-2"
                  onClick={() => setResetPasswordDialogOpen(true)}
                >
                  <i className="fa-solid fa-key"></i>
                  <span>Reset Password</span>
                </Button>
                {studentData?.lastPasswordReset ? (
                  <p className="text-sm text-neutral-500">
                    Last reset: {new Date(studentData.lastPasswordReset).toLocaleDateString()}
                  </p>
                ) : (
                  <p className="text-sm text-neutral-500">Never reset</p>
                )}
              </div>
            </div>
            {studentData?.emailConfirmed === false && studentData?.userId && (
              <div className="space-y-2">
                <Label>Ativação de email</Label>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start space-x-2"
                  onClick={async () => {
                    if (!studentData?.userId) return;
                    setResendEmailLoading(true);
                    try {
                      await resendConfirmationEmail({ userId: studentData.userId });
                      toast.success("Email de ativação reenviado.");
                      const updated = await getStudentById(studentId!);
                      setStudentData(updated);
                    } catch (e) {
                      toast.error(e instanceof Error ? e.message : "Falha ao reenviar email de ativação.");
                    } finally {
                      setResendEmailLoading(false);
                    }
                  }}
                  disabled={resendEmailLoading}
                >
                  <i className="fa-solid fa-envelope"></i>
                  <span>{resendEmailLoading ? "Enviando..." : "Reenviar email de ativação"}</span>
                </Button>
                <p className="text-sm text-neutral-500">Estudante ainda não confirmou o email.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={statusValue} name="status" onValueChange={(value) => handleStatusChange(value)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div id="purchased-packages" className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="packages">Purchased Packages</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsPackageDialogOpen(true)}
              >
                <i className="fa-solid fa-plus mr-1"></i>
                Adicionar pacotes
              </Button>
            </div>
            {/* Search input for packages */}
            <div className="mb-2">
              <Input
                type="text"
                placeholder="Search packages..."
                value={packageSearchTerm}
                onChange={(e) => setPackageSearchTerm(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {studentData?.packages
                ?.filter((pkg) =>
                  pkg.name.toLowerCase().includes(packageSearchTerm.toLowerCase())
                )
                .map((pkg: StudentPackage) => (
                  <AdminPurchasedPackageCard
                    key={pkg.id}
                    pkg={pkg}
                    studentId={studentId}
                    onRemove={(pkg: StudentPackage) => {
                      setSelectedPackage(pkg);
                      setRemoveDialogOpen(true);
                    }}
                  />
                ))}
              {(!studentData?.packages || studentData.packages.length === 0) && (
                <p className="col-span-full text-sm text-neutral-500 py-4">
                  Nenhum pacote ainda. Use &quot;Adicionar pacotes&quot; para atribuir pacotes a este estudante.
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="progress">Overall Progress</Label>
              <div className="flex items-center space-x-3">
                <div className="flex-1 bg-neutral-200 rounded-full h-2">
                  <div
                    className="bg-neutral-800 rounded-full h-2"
                    style={{ width: `${studentData?.progress?.overallProgress}%` }}
                  />
                </div>
                <span className="text-sm text-neutral-600">
                  {studentData?.progress?.overallProgress && studentData.progress.overallProgress > 0
                    ? `${studentData.progress.overallProgress}%`
                    : "No progress yet"}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="registration-date">Registration Date</Label>
              <Input
                id="registration-date"
                type="text"
                defaultValue={(() => {
                  if (!studentData?.enrollmentDate || 
                      studentData.enrollmentDate === "N/A" || 
                      studentData.enrollmentDate.startsWith("0001")) {
                    return "N/A";
                  }
                  try {
                    const date = new Date(studentData.enrollmentDate);
                    if (isNaN(date.getTime()) || date.getFullYear() <= 1) {
                      return "N/A";
                    }
                    return date.toLocaleDateString();
                  } catch {
                    return "N/A";
                  }
                })()}
                readOnly
                className="bg-neutral-50"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              rows={20}
              value={notes}
              onChange={handleNotesChange}
            />
          </div>
        </div>
        </AutoSaveForm>
        <div id="actions" className="mt-8 flex items-center justify-between pt-6 border-t">
          <div className="flex space-x-4">
            <Button variant="outline" className="space-x-2" onClick={() => setBlockAccessDialogOpen(true)}>
              <i className="fa-solid fa-ban"></i>
              <span>Block Access</span>
            </Button>
          </div>
          <div className="flex space-x-4">
            <Button variant="outline" onClick={() => window.history.back()}>Cancel</Button>
            <Button variant="default" onClick={handleSaveChanges} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>

        <PackageSelectionDialog
          isOpen={isPackageDialogOpen}
          onClose={() => setIsPackageDialogOpen(false)}
          onConfirm={async (selectedPackages: Package[]) => {
            if (!studentId) return;
            try {
              await addPackagesToStudent(
                studentId,
                selectedPackages.map((pkg) => pkg.id)
              );
              const updatedStudent = await getStudentById(studentId);
              setStudentData(updatedStudent);
              toast.success("Packages added successfully");
            } catch (error) {
              console.error("Error adding packages:", error);
              toast.error("Failed to add packages");
            }
          }}
        />

        <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove Package</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove {selectedPackage?.name} from this student? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRemoveDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (selectedPackage) {
                    handleRemovePackage(selectedPackage.id);
                    setRemoveDialogOpen(false);
                    setSelectedPackage(null);
                  }
                }}
              >
                Remove Package
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Block Access Confirmation Dialog */}
        <Dialog open={blockAccessDialogOpen} onOpenChange={setBlockAccessDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Block</DialogTitle>
              <DialogDescription>
                Are you sure you want to block this user? This action will prevent them from interacting with you.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setBlockAccessDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleBlockAccess}
              >
                Yes, Block User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Are you sure you want to reset this student's password? They will receive an email with a temporary password.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setResetPasswordDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  if (studentId) {
                    try {
                      const updatedStudent = await resetStudentPassword(studentId);
                      setStudentData(updatedStudent);
                      toast.success("Password reset successfully");
                    } catch (error) {
                      console.error("Error resetting password:", error);
                      toast.error("Failed to reset password");
                    }
                    setResetPasswordDialogOpen(false);
                  }
                }}
              >
                Reset Password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </AdminPageLayout>
  );
};

export default EditStudent;
